import type { SuggestedChip } from '@/components/ui/transcript-chips/types';
import { ClinicalTaskCategory } from '@heidi/schema/ml-scribe';
import type { ClinicalTask } from '@heidi/tasks/contracts';

/** Max suggestions shown in the banner carousel. */
export const DOCUMENT_SUGGESTION_BANNER_MAX_ITEMS = 4;

/** Prefix for `SuggestedChip.id` on document-draft clinical-task suggestions. */
export const DOCUMENT_SUGGESTION_CHIP_ID_PREFIX = 'clinical-task-doc-';

/** Resolves clinical task id for dismissal / analytics when `sourceTaskId` may be absent. */
export function getDocumentSuggestionTaskIdFromChip(
  chip: Pick<SuggestedChip, 'id' | 'sourceTaskId'>
): string | undefined {
  if (chip.sourceTaskId) return chip.sourceTaskId;
  if (chip.id.startsWith(DOCUMENT_SUGGESTION_CHIP_ID_PREFIX)) {
    return chip.id.slice(DOCUMENT_SUGGESTION_CHIP_ID_PREFIX.length);
  }
  return undefined;
}

export type DocumentSuggestionPayload = {
  taskId: string;
  /** Banner headline from clinical task `short_title` (trimmed; UI may truncate for layout). */
  bannerTitle: string;
  /** Second line from clinical task `title` (trimmed; UI may truncate for layout). */
  bannerSubtitle: string;
  /** Primary CTA label from clinical task `doc_suggestion_cta` when set. */
  cta: string | null;
  /** Evidence user message: mirrors the clinical task title. */
  evidencePrompt: string;
  /** Short label for suggestion chips (`chip_label`, falling back to titles). */
  chipLabel: string;
  /** Clinical task category (e.g. `document`, `communicate`). */
  category: ClinicalTaskCategory;
};
/**
 * Whether this task should surface in the document-creation suggestion banner.
 * Always true for `document` category; otherwise requires heuristic match on text.
 */
export function isDocumentCreationTask(task: ClinicalTask): boolean {
  const category = task.category;
  const isDocumentationCategory =
    category === ClinicalTaskCategory.document ||
    category === ClinicalTaskCategory.communicate;
  const hasDocumentCta = task.doc_suggestion_cta != null;

  return isDocumentationCategory && hasDocumentCta;
}

/**
 * User message sent to Evidence to draft the artefact from session context.
 */
export function buildEvidencePromptForDocumentTask(task: ClinicalTask): string {
  const bannerTitle = (task.short_title || '').trim();
  const taskTitle = (task.title || '').trim();
  const extra = [task.description, task.source_quote].filter(Boolean).join('\n\n').trim();

  return [
    'Using this session’s clinical note and transcript as context, please draft the following for clinical use.',
    `Requested ${bannerTitle}: ${taskTitle}`,
    extra ? `Additional details from the consultation:\n${extra}` : '',
    'Write a complete, professional draft ready for review and minor edits.',
  ]
    .filter(Boolean)
    .join('\n\n');
}

function mapPayload(task: ClinicalTask): DocumentSuggestionPayload {
  const ctaRaw = (task.doc_suggestion_cta ?? '').trim();
  const taskTitle = (task.title || '').trim();
  const bannerTitle = (task.short_title ?? '').trim();
  const chipFromTask = (task.chip_label ?? '').trim();
  const chipLabel = chipFromTask || bannerTitle || taskTitle;
  return {
    taskId: task.id,
    bannerTitle,
    bannerSubtitle: (task.title ?? '').trim(),
    cta: ctaRaw || null,
    evidencePrompt: taskTitle || buildEvidencePromptForDocumentTask(task),
    chipLabel,
    category: task.category as ClinicalTaskCategory,
  };
}

/**
 * Maps a document suggestion to a transcript-style chip for the suggestions peek sheet
 * (experiment `chips` variant).
 */
export function documentSuggestionPayloadToSuggestedChip(
  payload: DocumentSuggestionPayload
): SuggestedChip {
  return {
    id: `${DOCUMENT_SUGGESTION_CHIP_ID_PREFIX}${payload.taskId}`,
    label: payload.chipLabel,
    query: payload.evidencePrompt,
    category: 'document_draft',
    /** Below auto-query relevance threshold so these are never auto-queried. */
    relevance: 0.5,
    sourceTaskId: payload.taskId,
    title: payload.bannerTitle,
  };
}

/**
 * Label shown on peek-sheet / chip UI. When there is exactly one visible suggestion chip,
 * document drafts use `short_title` (`title`) instead of the compact `chip_label`.
 */
export function getSuggestedChipPeekDisplayLabel(
  chip: SuggestedChip,
  options: { soleVisibleSuggestedChip: boolean }
): string {
  const banner = chip.title?.trim();
  if (options.soleVisibleSuggestedChip && chip.category === 'document_draft' && banner) {
    return banner;
  }
  return chip.label;
}

export function clinicalTaskToDocumentSuggestion(
  task: ClinicalTask
): DocumentSuggestionPayload | null {
  if (task.is_suggestion_dismissed) {
    return null;
  }
  if (!isDocumentCreationTask(task)) {
    return null;
  }
  return mapPayload(task);
}

export function filterDocumentCreationSuggestions(
  tasks: ClinicalTask[],
  maxItems: number = DOCUMENT_SUGGESTION_BANNER_MAX_ITEMS
): DocumentSuggestionPayload[] {
  const out: DocumentSuggestionPayload[] = [];
  for (const task of tasks) {
    if (out.length >= maxItems) break;
    const mapped = clinicalTaskToDocumentSuggestion(task);
    if (mapped) {
      out.push(mapped);
    }
  }
  return out;
}
